import { useState } from "react";
import { Copy, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/csv";

/**
 * Small square copy button. Shows check mark for 1.2s after copy.
 * Variants:
 *   - "ghost"  (default): borderless, muted, hover-tint. Sits inline with content.
 *   - "solid"           : bordered, for section headers / prominent placements.
 */
export const CopyButton = ({
    text,
    label = "Copy",
    variant = "ghost",
    size = 14,
    className = "",
    dataTestId,
}) => {
    const [copied, setCopied] = useState(false);

    const handle = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const ok = await copyToClipboard(text);
        if (ok) {
            setCopied(true);
            const cleanLabel = label.replace(/^copy\s+/i, "").toLowerCase() || "text";
            toast.success(`Copied ${cleanLabel}`);
            setTimeout(() => setCopied(false), 1200);
        } else {
            toast.error("Copy failed");
        }
    };

    const base =
        variant === "solid"
            ? "border border-ink bg-white text-ink hover:bg-ink hover:text-white h-7 px-2 text-[10px]"
            : "border border-transparent text-neutral-400 hover:border-neutral-300 hover:text-ink h-7 w-7 justify-center";

    return (
        <button
            data-testid={dataTestId}
            onClick={handle}
            aria-label={label}
            title={label}
            className={`inline-flex items-center gap-1 font-mono uppercase tracking-widest transition-colors duration-150 ${base} ${className}`}
        >
            {copied ? (
                <Check size={size} weight="bold" />
            ) : (
                <Copy size={size} weight="bold" />
            )}
            {variant === "solid" && (
                <span className="hidden sm:inline">
                    {copied ? "Copied" : label}
                </span>
            )}
        </button>
    );
};
